import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Container as MuiContainer,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from "@mui/material";
import { CourseSelection } from "./CourseSelection";

export const HeroSection = ({
  universitiesData,
  loadingUniversites,
  courseOptions,
  loadingCourses,
  formValues,
  onUniversityChange,
  onCourseInputChange,
  onCourseSelected,
  backgroundImage,
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [isMounted, setIsMounted] = useState(false);
  const [animationError, setAnimationError] = useState(false);
  const slideRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Fallback for animation errors
  const handleAnimationError = () => {
    setAnimationError(true);
  };

  return (
    <Box
      data-testid="landing-page-background"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: { xs: 'cover', md: 'cover' },
        backgroundPosition: { xs: 'center top', md: 'center' },
        backgroundRepeat: 'no-repeat',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(232, 121, 117, 0.5) 0%, rgba(248, 107, 107, 0.0001) 100%)',
          zIndex: 1,
        }
      }}
    >
      <MuiContainer
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: { xs: 4, md: 8 },
          px: { xs: 2, sm: 3, md: 4 },
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {isMounted && !animationError && (
          <Fade in={isMounted} timeout={1000} onError={handleAnimationError}>
            <Box sx={{ mb: { xs: 4, md: 6 } }}>
              <Typography
                variant={isMobile ? "h3" : "h2"}
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  mb: 2,
                  lineHeight: 1.2,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  maxWidth: '100%',
                }}
              >
                Nachhilfe und Lerngruppen in deinem Uni-Kurs
              </Typography>
            </Box>
          </Fade>
        )}
        
        {animationError && (
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                mb: 2,
                lineHeight: 1.2,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
                maxWidth: '100%',
              }}
            >
              Nachhilfe und Lerngruppen in deinem Uni-Kurs
            </Typography>
          </Box>
        )}

        {isMounted && !animationError && (
          <Slide direction="up" in={isMounted} timeout={1200} ref={slideRef} onError={handleAnimationError}>
            <div>
              <CourseSelection
                universitiesData={universitiesData}
                loadingUniversites={loadingUniversites}
                courseOptions={courseOptions}
                loadingCourses={loadingCourses}
                formValues={formValues}
                onUniversityChange={onUniversityChange}
                onCourseInputChange={onCourseInputChange}
                onCourseSelected={onCourseSelected}
              />
            </div>
          </Slide>
        )}
        
        {animationError && (
          <div>
            <CourseSelection
              universitiesData={universitiesData}
              loadingUniversites={loadingUniversites}
              courseOptions={courseOptions}
              loadingCourses={loadingCourses}
              formValues={formValues}
              onUniversityChange={onUniversityChange}
              onCourseInputChange={onCourseInputChange}
              onCourseSelected={onCourseSelected}
            />
          </div>
        )}
      </MuiContainer>
    </Box>
  );
};
